import { config } from "../shared/config";
import type { RequestOptions } from "@/lib/request/request-options";
import { resolveTimeout } from "@/lib/timeout/resolve-timeout";
import { authHeaders } from "@/lib/auth/auth-headers";
import { clientHeaders } from "@/lib/version/client-headers";
import { retryAfterSeconds } from "@/lib/retry/retry-after-seconds";
import { fetchResponse } from "@/lib/response/fetch-response";
import { addQueryParameters } from "@/lib/api/add-query-parameters";
import { parseResponsePayload } from "@/lib/api/parse-response-payload";
import { throwResponseError } from "@/lib/api/throw-response-error";

/**
 * The one place the CLI talks to the platform.
 *
 * Every command names its REST path once and gets its types from the router
 * (api-inputs.ts), so there is no generated client to keep in step and no
 * second copy of the API surface.
 */
export async function api<T>(
  requestPath: string,
  options: RequestOptions = {},
): Promise<T> {
  const baseUrl = options.as?.baseUrl ?? config.load().baseUrl;
  const url = new URL(`${baseUrl.replace(/\/$/, "")}/api/v1${requestPath}`);
  addQueryParameters(url, options.query);

  // Resolved before the try: a missing credential is a usage error, not a
  // network failure, and must not be reported as one.
  const headers = {
    ...clientHeaders(),
    ...authHeaders(options.as),
    ...(options.body === undefined
      ? {}
      : { "content-type": "application/json" }),
  };

  const method = options.method ?? "GET";
  const deadline = resolveTimeout(options.timeoutMs);
  const [response, text] = await fetchResponse({
    url,
    method,
    headers,
    body: options.body,
    deadline,
    baseUrl,
  });

  const retryAfter = retryAfterSeconds(response);
  const payload = parseResponsePayload(response, text, {
    method,
    url,
    retryAfter,
  });

  if (!response.ok) {
    throwResponseError(response, payload, retryAfter);
  }

  return payload as T;
}
